/**
 * What the receiver draws for a snapshot and its putt event: the course
 * presentation (3D stage, mode HUD, summary, transition, and result cards),
 * the card and ambient surfaces, and the hints over them. Shared by the TV
 * page (App.tsx, fed by the tv_sessions document) and the app's browser build
 * (app/LagKing/src/tv/BrowserTvLayer.web.tsx, fed in-process by the app's own
 * publishes: in the browser the app is its own TV, Cole 2026-10-28). Nothing
 * here touches the network or the session.
 */
import { Suspense, lazy, useEffect, useState } from 'react';
import StageLoading from './StageLoading';
import AmbientOverlay, { type SessionMetrics } from './AmbientOverlay';
import SummaryCard from './SummaryCard';
import TransitionOverlay from './TransitionOverlay';
import GameResultOverlay from './GameResultOverlay';
import { GatePill, NoticeStrip, SleepFooter, WaitingForPhone } from './StatusHints';
import type { TvNotice, TvPuttEvent, TvSnapshot } from './protocol';
import { isFreshPutt, presentationKind, sleepFooter } from './presentation';
import { isGhostPutt, noticeVisible } from './hudModel';
import { stageSceneForEvent } from './puttAnimation';
import SoundUnlock from './SoundUnlock';
import { replayRollMs } from '../../../packages/putt-scene/src/replayTiming';
import { FT_PER_M } from '../../../packages/putt-scene/src/puttMath';

// The 3D stage (three.js, the shared scene) is its own chunk: the pairing
// and start screens paint from the small first bundle, and the stage code
// and the likely biome's assets load behind them (README, "Loading and
// preload").
const PuttStage = lazy(() => import('./PuttStage'));
const stageFallback = (
  <div className="stage" aria-label="LagKing 3D putting view">
    <StageLoading progress={0.1} />
  </div>
);

/**
 * The interrupt the TV should show right now (Part 2, "Interrupts"). A
 * notice with `expiresAtMs` hides itself locally at that time, without
 * waiting for the phone to publish again.
 */
export function useLiveNotice(notice: TvNotice | null | undefined): TvNotice | null {
  const [now, setNow] = useState(() => Date.now());
  const expiresAtMs = notice?.expiresAtMs ?? null;
  useEffect(() => {
    setNow(Date.now());
    if (expiresAtMs == null || !Number.isFinite(expiresAtMs)) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, expiresAtMs - Date.now()) + 20);
    return () => window.clearTimeout(timer);
  }, [expiresAtMs, notice?.id]);
  return noticeVisible(notice, now) ? notice ?? null : null;
}

/** Putts seen this session, for the metrics readout's "N putts · avg". */
export interface SessionTally {
  mode: string | null;
  ids: string[];
  proximities: number[];
}

export const EMPTY_TALLY: SessionTally = { mode: null, ids: [], proximities: [] };

/**
 * The id of the putt whose roll and review have fully played out. Summary
 * and transition cards wait for it so they never cover the ball mid-roll.
 */
export function useSettledEventId(event: TvPuttEvent | null): string | null {
  const [settledEventId, setSettledEventId] = useState<string | null>(null);
  useEffect(() => {
    if (!event || event.kind !== 'putt') return;
    // The scene's own stop time (replayTiming.ts): the old 6.5 s cap let a
    // card cover a long putt before it stopped.
    const rollMs = replayRollMs(event.speedMps * FT_PER_M, Math.max(0, event.distanceFt));
    const settleAtMs = event.createdAtMs + rollMs + 4700;
    const timer = window.setTimeout(() => setSettledEventId(event.id), Math.max(0, settleAtMs - Date.now()));
    return () => window.clearTimeout(timer);
  }, [event?.createdAtMs, event?.distanceFt, event?.id, event?.speedMps]);
  return settledEventId;
}

/**
 * Session tally for the metrics readout, reset when the mode changes. A
 * ghost (an opponent's replayed putt) never counts as the phone's putt.
 * `reset` empties it (App.tsx: forgetting the TV's account).
 */
export function useSessionTally(event: TvPuttEvent | null, mode: string | null): { tally: SessionTally; reset: () => void } {
  const [tally, setTally] = useState<SessionTally>(EMPTY_TALLY);
  useEffect(() => {
    if (!event || event.kind !== 'putt' || isGhostPutt(event)) return;
    setTally((current) => {
      const base = current.mode === mode ? current : { mode, ids: [], proximities: [] };
      if (base.ids.includes(event.id)) return base;
      return {
        mode,
        ids: [...base.ids, event.id],
        proximities: event.proximityFt != null
          ? [...base.proximities, Math.abs(event.proximityFt)]
          : base.proximities,
      };
    });
  }, [event, mode]);
  return { tally, reset: () => setTally(EMPTY_TALLY) };
}

export function renderPresentation(input: {
  snapshot: TvSnapshot | null;
  event: TvPuttEvent | null;
  tvLinkFresh: boolean;
  settled: boolean;
  reconnecting: boolean;
  cachedCourse: TvSnapshot | null;
  /** The visible interrupt, already through `noticeVisible`. */
  notice: TvNotice | null;
  tally: SessionTally;
  /** "Pair with a different account" on the waiting overlay. */
  onForget?: (() => void) | null;
  /** The same on the idle start screen of a paired TV. */
  idleForget?: (() => void) | null;
  /** The account this TV is paired to. */
  ownerHandle?: string | null;
}) {
  const { event, tvLinkFresh, settled, reconnecting, cachedCourse, notice, tally, onForget, idleForget = null, ownerHandle = null } = input;
  // Every layer below reads the notice from the snapshot, so an expired one
  // is gone from the pills as well as the strip. The kind is decided before
  // the notice is looked at: a notice never changes the presentation.
  const snapshot: TvSnapshot | null = input.snapshot ? { ...input.snapshot, notice } : input.snapshot;
  const freshEvent = event && !isGhostPutt(event) && isFreshPutt(event.createdAtMs, Date.now()) ? event : null;
  const kind = presentationKind(snapshot, { freshPutt: freshEvent !== null });
  const sceneNotice = <NoticeStrip notice={notice} placement="scene" />;
  const hints = (
    <>
      {kind === 'mirror' || kind === 'ambient' ? null : <SleepFooter text={sleepFooter(snapshot)} />}
      {!tvLinkFresh || reconnecting ? <WaitingForPhone reconnecting={reconnecting} onForget={onForget} ownerHandle={ownerHandle} /> : null}
      <SoundUnlock revision={snapshot?.sound} />
    </>
  );

  if (kind === 'course') {
    const summary = settled ? snapshot?.summary ?? null : null;
    const transition = settled ? snapshot?.transition ?? null : null;
    // A ghost putt rolls in the opponent's color: swap the scene's ball color
    // for that event only. The stage reads the color from the scene.
    const stageScene = stageSceneForEvent(
      snapshot?.scene?.kind === 'putt' ? snapshot.scene : null,
      event,
    ) ?? snapshot?.scene ?? null;
    return (
      <>
        <div className="stage-slot">
          <Suspense fallback={stageFallback}>
            <PuttStage event={event} snapshot={{ ...snapshot, scene: stageScene, tvLinkFresh }} />
          </Suspense>
        </div>
        {summary ? <SummaryCard summary={summary} /> : null}
        {transition ? <TransitionOverlay transition={transition} courseHint={courseHint(snapshot, cachedCourse)} /> : null}
        <GameResultOverlay snapshot={snapshot} />
        {sceneNotice}
        {hints}
      </>
    );
  }

  // No course behind the start screen or the cards (Cole, 2026-09-25: "I dont
  // want a course in the background here"). The 3D stage draws only when the
  // phone sends a scene; everything else sits on the branded backdrop.
  const stage = <div className="tv-backdrop" aria-hidden="true" />;

  if (kind === 'card') {
    const summary = snapshot?.summary ?? null;
    const transition = snapshot?.transition ?? null;
    return (
      <>
        {stage}
        <GatePill snapshot={snapshot} className="companion-gate ambient-gate" />
        {summary ? <SummaryCard summary={summary} /> : transition ? <TransitionOverlay transition={transition} courseHint={courseHint(snapshot, cachedCourse)} /> : null}
        <GameResultOverlay snapshot={snapshot} />
        {sceneNotice}
        {hints}
      </>
    );
  }

  // Menus are not mirrored (Cole, 2026-09-25, after the first paired session:
  // "I dont need to render menus in TV, only active game modes and game mode
  // transitions. Menu should be something like, start a game using your
  // device"). A menu on the phone shows the calm start screen; results and
  // transitions still render as cards.
  if (kind === 'mirror') {
    return (
      <>
        {stage}
        <AmbientOverlay snapshot={snapshot} metrics={null} notice={notice} startScreen ownerHandle={ownerHandle} onForget={tvLinkFresh && !reconnecting ? idleForget : null} />
        <GameResultOverlay snapshot={snapshot} />
        {hints}
      </>
    );
  }

  const metrics: SessionMetrics | null = kind === 'metrics'
    ? {
        event: freshEvent,
        sessionN: tally.ids.length,
        sessionAvgFt: tally.proximities.length
          ? tally.proximities.reduce((sum, value) => sum + value, 0) / tally.proximities.length
          : null,
      }
    : null;
  return (
    <>
      {stage}
      <AmbientOverlay snapshot={snapshot} metrics={metrics} notice={notice} ownerHandle={metrics ? null : ownerHandle} onForget={!metrics && tvLinkFresh && !reconnecting ? idleForget : null} />
      <GameResultOverlay snapshot={snapshot} />
      {hints}
    </>
  );
}


/** The Sim 9 course a scorecard belongs to: the stage's course, else the
 * last course this session showed, else the HUD's course name. */
function courseHint(snapshot: TvSnapshot | null, cachedCourse: TvSnapshot | null): { courseId: string | null; courseName: string | null } {
  const scene = snapshot?.scene?.kind === 'putt' ? snapshot.scene : cachedCourse?.scene?.kind === 'putt' ? cachedCourse.scene : null;
  const game = snapshot?.game ?? cachedCourse?.game ?? null;
  return {
    courseId: scene?.courseId ?? null,
    courseName: game && game.kind === 'sim9' ? game.courseName : null,
  };
}
